import React, { useCallback, useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { WidgetFormValues, CustomQuestion } from '@/lib/widget-schema'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl,
	FormDescription,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { CustomQuestionBuilder } from '@/components/widget-builder/CustomQuestionBuilder'
import { ThankYouMessage } from '@/components/widget-builder/ThankYouMessage'
import { useTranslation } from '@/hooks/useTranslation'

interface Step2Props {
	form: UseFormReturn<WidgetFormValues>
	isPremium: boolean
}

export function Step2_Content({ form, isPremium }: Step2Props) {
	const { t } = useTranslation()
	const showSurvey = form.watch('enable_survey')
	const showBugReporting = form.watch('enable_bug_reporting')
	const showContactMessages = form.watch('enable_contact_messages')
	const showFeatureRequests = form.watch('enable_feature_requests')
	const showReviews = form.watch('enable_reviews')
	const feedbackType = form.watch('primary_feedback_type')

	// Memoize the questions prop for CustomQuestionBuilder
	// Important: form.watch might return a new array reference on parent re-renders
	// This useMemo helps if watchedCustomQuestions reference itself becomes stable.
	const watchedCustomQuestions = form.watch('customQuestions')
	const customQuestionsForBuilder = useMemo(() => {
		return watchedCustomQuestions || []
	}, [watchedCustomQuestions])

	// Handler specifically for CustomQuestionBuilder, memoized
	const handleCustomQuestionsChange = useCallback(
		(newQuestions: CustomQuestion[]) => {
			const currentQuestions = form.getValues('customQuestions')
			// Only update if the data has actually changed to prevent unnecessary re-renders/loops
			if (JSON.stringify(newQuestions) !== JSON.stringify(currentQuestions)) {
				form.setValue('customQuestions', newQuestions, {
					shouldValidate: true,
					shouldDirty: true,
				})
			}
		},
		[form]
	)

	return (
		<Card>
			<CardHeader>
				<CardTitle>{t('widgetBuilder.step2.title')}</CardTitle>
				<CardDescription>
					{t('widgetBuilder.step2.description')}
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-6">
				<FormField
					control={form.control}
					name="header_title"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('widgetBuilder.step2.widgetHeaderTitle')}</FormLabel>
							<FormDescription>
								{t('widgetBuilder.step2.headerTitleDescription')}
							</FormDescription>
							<FormControl>
								<Input placeholder={t('widgetBuilder.step2.headerTitlePlaceholder')} {...field} />
							</FormControl>
						</FormItem>
					)}
				/>

				{showSurvey && (
					<div className="space-y-4 pt-4 border-t">
						<h3 className="text-md font-semibold text-foreground">
							{t('widgetBuilder.step2.feedbackSurveyContent')}
						</h3>
						{(feedbackType === 'nps' || feedbackType === 'csat') && (
							<FormField
								control={form.control}
								name="question"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('widgetBuilder.step2.mainQuestion')}</FormLabel>
										<FormDescription>
											{t('widgetBuilder.step2.mainQuestionDescription')}
										</FormDescription>
										<FormControl>
											<Input
												placeholder={
													feedbackType === 'nps'
														? t('widgetBuilder.step2.npsQuestionPlaceholder')
														: t('widgetBuilder.step2.csatQuestionPlaceholder')
												}
												{...field}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
						)}

						{feedbackType === 'custom' && (
							<div className="space-y-2">
								<FormLabel>{t('widgetBuilder.step2.customSurveyQuestions')}</FormLabel>
								<FormDescription>
									{t('widgetBuilder.step2.customSurveyQuestionsDescription')}
								</FormDescription>
								<CustomQuestionBuilder
									questions={customQuestionsForBuilder as any}
									onChange={handleCustomQuestionsChange}
								/>
							</div>
						)}

						{feedbackType === 'testimonial' && (
							<div className="rounded-lg border bg-muted/50 p-4">
								<p className="text-sm text-muted-foreground">
									{t('widgetBuilder.step2.testimonialInfo')}
								</p>
							</div>
						)}
						<FormField
							control={form.control}
							name="buttonText"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('widgetBuilder.step2.submitButtonText')}</FormLabel>
									<FormDescription>
										{t('widgetBuilder.step2.submitButtonTextDescription')}
									</FormDescription>
									<FormControl>
										<Input placeholder={t('widgetBuilder.step2.submitButtonPlaceholder')} {...field} />
									</FormControl>
								</FormItem>
							)}
						/>
					</div>
				)}

				<div className="space-y-4 pt-4 border-t">
					<h3 className="text-md font-semibold text-foreground">
						{t('widgetBuilder.step2.thankYouMessage')}
					</h3>
					<FormDescription>
						{t('widgetBuilder.step2.thankYouMessageDescription')}
					</FormDescription>
					<ThankYouMessage form={form} />
				</div>
			</CardContent>
		</Card>
	)
}
